import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { createMarkerHashedKey } from 'libraries/mapLibrary';
import { interactiveSitePlanSvg } from 'constants/svg-assets';
import { priceReducedSvgHtml, markerTipSvgHtml } from '../constants/svgIcons';
import MarkerType from '../enums/markerType';
import { isHousingDevelopmentViewed } from '../services/markerSessionService';
import getClassesToApply from '../services/unitMarkerService';

/* Creates a new instance of a housing development that will be plotted in the map as a marker.
 */
export default class HousingDevelopmentMarker {
  constructor(housing) {
    this.geoId = housing.GeoId;
    this.detailText = housing.DetailText;
    this.type = MarkerType.HousingDevelopment.toString();
    this.id = housing.Id;
    this.communityId = housing.Id;
    this.latitude = housing.Latitude;
    this.longitude = housing.Longitude;
    // TODO: NewHomeSource Rebrand
    // Change the class on the backend.
    this.priceText = housing.PriceText.replace(
      'small--text',
      'nhs-c-tooltip__price-label--small'
    );
    this.shortPrice = housing.ShortPrice;
    this.title = housing.Title;
    this.totalPlans = housing.TotalPlans;
    this.priceReducedText = housing.PriceReducedText;
    this.priceReducedTrackingLabel = housing.PriceReducedTrackingLabel;
    this.communityName = housing.Title;
    this.builderId = housing.BuilderId;
    this.pageNumber = housing.Page;
    this.priceLow = housing.PriceLow;
    this.priceHigh = housing.PriceHigh;
    this.hasInteractiveSitePlan = housing.HasInteractiveSitePlan;
    this.sitePlanCenterlatitude = housing.SitePlanCenterLatitude;
    this.sitePlanCenterlongitude = housing.SitePlanCenterLongitude;
    this.isViewed = isHousingDevelopmentViewed(housing.Id);
    this.isPriceReduction = !!housing.PriceReducedText;
    this.isBasic = housing.IsBasic === 1;
    this.isFeaturedListing = housing.IsFeaturedListing || false;
    this.isMasterPlannedCommunity = housing.IsMasterPlannedCommunity || false;
    this.isAdult = housing.IsAdult || false;
    this.isCallForPrice = housing.IsCallForPrice || false;

    if (this.isBasic) {
      this.zIndex = 1;
    } else if (this.isFeaturedListing) {
      this.zIndex = 3;
    } else {
      this.zIndex = 2;
    }
  }

  /**
   * getMarkerText: gets the text the marker is going to show once rendered.
   * @returns a text to show on the map once rendered.
   */
  getMarkerText() {
    if (!this.isCallForPrice) {
      return this.shortPrice;
    }
    return '';
  }

  /**
   * @returns The name of the extend marker type.
   */
  getType() {
    return this.type;
  }

  /**
   * getInteractiveSitePlanSection: adds a <p> with the information needed to reach
   * the interactive site plan of the marker, if any.
   */
  getInteractiveSitePlanSection() {
    if (!this.hasInteractiveSitePlan) {
      return '';
    }

    return `<button
                class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32 nhs-u-flex-row nhs-u-gap--200"
                data-interactive-plan-button
                data-latitude="${this.sitePlanCenterlatitude}"
                data-longitude="${this.sitePlanCenterlongitude}"
                data-community-id="${this.communityId}">
                <span class='nhs-c-icon--16'>${interactiveSitePlanSvg}</span> Interactive Map
            </button>`;
  }

  /**
   * @returns {string} The HTML string for the card element to be associated to this housing development marker.
   */
  getCardElement() {
    return `
      <div class="nhs-c-tooltip--map"
          data-id="${this.id}"
          data-tooltip="true"
          data-marker-styles="${this.getMarkerStyles().join(' ')}">
        <div class="nhs-c-tooltip__title-container">${this.priceText}</div>
        <p>${this.title}</p>
        ${this.getFacts()}
        ${this.getInteractiveSitePlanSection()}
      </div>
    `;
  }

  /**
   * @returns {string} The HTML string for the card element to be associated to this housing development marker,
   * including price reduction information.
   */
  getCardElementWithPriceReduction() {
    return `
      <div class="nhs-c-tooltip--map"
          data-id="${this.id}"
          data-tooltip="true"
          data-marker-styles="${this.getMarkerStyles().join(' ')}">
        <div class="nhs-c-tooltip__title-container">${this.priceText}</div>
        <p>${this.title}</p>
        <p data-url="${this.getDetailUrl()}#HomesAndPlans" class="nhs-c-tooltip__price-reduction" data-qa="price-reduction">
          <span class='nhs-c-tooltip__price-reduction-icon'>${priceReducedSvgHtml}</span>
          ${this.priceReducedText}
        </p>
        ${this.getFacts()}
        ${this.getInteractiveSitePlanSection()}
      </div>
    `;
  }

  getFacts() {
    if (this.isCallForPrice) {
      return '';
    }

    return `<p class='nhs-c-tooltip__facts'>${this.detailText}</p>`;
  }

  /**
   * It returns a string that is the url to the detail page of the community
   * @returns The string `/communitydetail/community-${this.id}`
   */
  getDetailUrl() {
    return `/communitydetail/builder-${this.builderId}/community-${this.id}`;
  }

  /**
   * This function returns a new GoogleAnalyticsEvent object with the event category, event action, and
   * event label set.
   * @returns A new instance of the GoogleAnalyticsEvent class.
   */
  getGoogleAnalyticsEventForClicks() {
    const customDimensions = {
      [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]:
        this.priceReducedTrackingLabel,
      [GoogleAnalyticsCustomDimensions.CommunityName().getName()]:
        this.communityName,
      [GoogleAnalyticsCustomDimensions.CommunityId().getName()]: this.id,
      [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: !this.isBasic
    };

    const url = this.getDetailUrl();
    return new GoogleAnalyticsEvent(
      'Map-Pin-Clicks',
      'Communities: SRP-Map-Comm-Clicks',
      url,
      customDimensions
    );
  }

  /**
   * This function returns a new meta data object with the required information based on the map pin data.
   * @returns A new instance of the meta data object.
   */
  getHousingLocationMetaData() {
    return {
      priceReducedTrackingLabel: this.priceReducedTrackingLabel,
      communityName: this.communityName,
      communityId: this.id,
      isPaid: !this.isBasic,
      priceLow: this.priceLow,
      priceHigh: this.priceHigh,
      hasInteractiveSitePlan: this.hasInteractiveSitePlan,
      id: this.id
    };
  }

  /**
   * This function returns the HTML in charge of build the hover card of a given marker
   * @returns hover card HTML
   */
  getCard() {
    if (this.priceReducedText) {
      return this.getCardElementWithPriceReduction();
    }

    return this.getCardElement();
  }

  /**
   * @returns {string} The complete HTML that represents the housing development marker
   * as an icon and a card with details.
   */
  getIcon() {
    return `
      <div class="nhs-c-advance-marker nhs-c-tooltip-hide nhs-mq-tooltip-show--large">
        ${this.getHtmlPinElement()}
        ${this.getCard()}
      </div>
    `;
  }

  /**
   * getShortPriceIconSection: gets a div with the short price to show in the marker icon.
   * @returns a div with the short price to show in the marker icon.
   */
  getShortPriceIconSection() {
    return this.getMarkerText()
      ? `<div data-marker-text data-qa="marker_price" class="nhs-c-marker__text">${this.getMarkerText()}</div>`
      : '';
  }

  /**
   * getDescription: returns a description that will help screen readers
   * provide a better explanation about the marker itself.
   * @returns a description for the marker.
   */
  getDescription() {
    if (this.shortPrice) {
      return `Homes from ${this.shortPrice}`;
    }

    return this.title;
  }

  /**
   * getMarkerStyles function is in charge of returning an array with the classes to use for the map marker.
   * @returns an array representing all the classes we should apply to the map marker in terms of its characteristics.
   */
  getMarkerStyles() {
    const { base } = getClassesToApply({
      isMasterPlannedCommunity: this.isMasterPlannedCommunity,
      isSpotlight: this.isFeaturedListing,
      isAdult: this.isAdult,
      isViewed: this.isViewed
    });

    const classesToApply = [...base].filter(Boolean);
    return classesToApply;
  }

  /**
   * Returns the HTML string representing a pin marker of the housing development.
   * @returns {string} The HTML string for the marker pin.
   */
  getHtmlPinElement() {
    const markerStyles = this.getMarkerStyles().join(' ');
    const dataAttributes = [
      `data-marker-icon="true"`,
      `class="${markerStyles}"`,
      `data-id="${this.id}"`,
      `data-type="${this.getType()}"`,
      `data-geographic-key="${createMarkerHashedKey(
        this.latitude,
        this.longitude
      )}"`,
      `data-price-reduced-tracking-label="${this.priceReducedTrackingLabel}"`,
      `data-is-spotlight="${this.isFeaturedListing}"`,
      `data-is-mpc="${!!this.isMasterPlannedCommunity}"`,
      `data-is-adult="${!!this.isAdult}"`,
      `data-is-no-price="${!!this.isCallForPrice}"`,
      `data-viewed="${!!this.isViewed}"`,
      `data-is-price-reduction="${this.isPriceReduction}"`,
      `data-qa="marker_container"`,
      this.isBasic ? `data-is-basic="true"` : `data-is-paid="true"`
    ].join(' ');

    if (!this.shortPrice || this.isCallForPrice) {
      return `<div ${dataAttributes}></div>`;
    }

    return `
      <div ${dataAttributes}>
        <div class="nhs-c-marker">
          <div class="nhs-c-marker__box">
            <div data-marker-text data-qa="marker_price" class="nhs-c-marker__text">${this.shortPrice}</div>          
            <div class="nhs-c-marker__arrow--tip">${markerTipSvgHtml}</div>
          </div>
        </div>
      </div>
    `;
  }
}
